import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import InitialAvatar from "@/components/InitialAvatar";
import PremiumGate from "@/components/PremiumGate";
import SitterCard from "@/components/SitterCard";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import Seo from "@/components/Seo";
import { toast } from "sonner";
import {
  Star, Check, X, ShieldCheck, Clock, Sparkles, Search, Heart, MessageCircle,
  CalendarCheck, Wallet, User, TrendingUp, Send, ChevronRight,
} from "lucide-react";

const AVAIL = ["Weekdays", "Weekends", "Mornings", "Afternoons", "Evenings", "Overnight"];
const LANGS = ["English", "Arabic", "French", "Urdu", "Spanish", "Turkish", "Somali", "Farsi", "Bengali"];

export default function Dashboard() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  useEffect(() => { if (!loading && !user) nav("/login"); }, [user, loading, nav]);
  if (loading || !user) return <div className="min-h-screen bg-linen"><Nav /><div className="px-5 py-20 text-forest/60 text-center">Loading…</div></div>;
  return (
    <div className="min-h-screen bg-linen">
      <Seo title="Your dashboard" path="/dashboard" noindex />
      <Nav />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <span className="badge-sage">Your dashboard</span>
            <h1 className="font-serif text-4xl md:text-5xl text-forest mt-3">Hi {user.name.split(" ")[0]}.</h1>
            <p className="text-forest/70 mt-2">
              Signed in as <b className="text-forest">{user.role}</b>
              {user.role === "parent" && (
                user.is_premium
                  ? <span className="badge-gold ml-2" data-testid="dash-premium"><Sparkles className="w-3 h-3" /> Premium</span>
                  : <span className="badge-sage ml-2">Free plan</span>
              )}
            </p>
          </div>
          {user.role === "parent" && !user.is_premium && (
            <Link to="/pricing" className="btn-primary text-sm" data-testid="dash-upgrade">Upgrade to Premium</Link>
          )}
        </div>
        <div className="mt-10">
          {user.role === "parent" && <ParentDashboard />}
          {user.role === "babysitter" && <CaregiverDashboard />}
          {user.role === "admin" && <AdminDash />}
        </div>
      </div>
      <Footer />
    </div>
  );
}

// ---- Parent Dashboard ----
function ParentDashboard() {
  const [tab, setTab] = useState("search");
  const tabs = [
    { k: "search", l: "Search", icon: Search },
    { k: "favorites", l: "Saved", icon: Heart },
    { k: "messages", l: "Messages", icon: MessageCircle },
    { k: "bookings", l: "Bookings", icon: CalendarCheck },
    { k: "membership", l: "Membership", icon: Sparkles },
  ];
  return (
    <section data-testid="parent-dash" className="space-y-6">
      <div className="flex flex-wrap gap-2 p-1 bg-taupe/30 rounded-full w-fit">
        {tabs.map((t) => (
          <button key={t.k} onClick={() => setTab(t.k)} data-testid={`ptab-${t.k}`}
            className={`px-4 py-2 rounded-full text-sm flex items-center gap-1.5 transition-colors ${tab === t.k ? "bg-forest text-linen" : "text-forest/70"}`}>
            <t.icon className="w-4 h-4" /> {t.l}
          </button>
        ))}
      </div>
      {tab === "search" && <ParentSearch />}
      {tab === "favorites" && <ParentFavorites />}
      {tab === "messages" && <MessagesPanel />}
      {tab === "bookings" && <ParentBookings />}
      {tab === "membership" && <MembershipPanel />}
    </section>
  );
}

function ParentSearch() {
  const { user } = useAuth();
  const [form, setForm] = useState({ postal_code: "", languages: [], availability: [], min_rating: 0, cpr_required: false, police_required: false });
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);
  const toggle = (list, v) => list.includes(v) ? list.filter((x) => x !== v) : [...list, v];
  const search = async () => {
    if (!user.is_premium) return;
    setBusy(true);
    try { const { data } = await api.post("/match", form); setResults(data); }
    catch (er) { toast.error(formatApiErrorDetail(er.response?.data?.detail)); }
    setBusy(false);
  };
  if (!user.is_premium) return <PremiumGate title="AI matching is a Premium feature" body="Tell us about your family and we'll match you with your best-fit caregivers using our AI matcher." />;
  return (
    <div className="space-y-6">
      <div className="card-organic p-6" data-testid="ai-match-form">
        <div className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-gold" />
          <h2 className="font-serif text-2xl text-forest">AI caregiver matching</h2>
        </div>
        <p className="text-sm text-forest/70 mt-1">Give us your preferences and we'll rank the best caregivers for your family.</p>
        <div className="grid md:grid-cols-3 gap-4 mt-5">
          <div>
            <label className="text-[11px] uppercase tracking-widest text-forest/60">Postal code</label>
            <input value={form.postal_code} onChange={(e) => setForm({ ...form, postal_code: e.target.value.toUpperCase() })} className="input-organic mt-1" data-testid="match-postal" />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-widest text-forest/60">Min rating</label>
            <select value={form.min_rating} onChange={(e) => setForm({ ...form, min_rating: Number(e.target.value) })} className="input-organic mt-1">
              <option value="0">Any</option><option value="4.5">4.5+</option><option value="4.7">4.7+</option><option value="4.9">4.9+</option>
            </select>
          </div>
          <div className="flex items-end gap-4">
            <label className="flex items-center gap-2 text-sm text-forest/80"><input type="checkbox" checked={form.cpr_required} onChange={(e) => setForm({ ...form, cpr_required: e.target.checked })} className="accent-forest w-4 h-4" /> CPR</label>
            <label className="flex items-center gap-2 text-sm text-forest/80"><input type="checkbox" checked={form.police_required} onChange={(e) => setForm({ ...form, police_required: e.target.checked })} className="accent-forest w-4 h-4" /> Police check</label>
          </div>
        </div>
        <div className="mt-4">
          <div className="text-[11px] uppercase tracking-widest text-forest/60">Languages</div>
          <div className="flex flex-wrap gap-2 mt-2">
            {LANGS.map((l) => (
              <button key={l} onClick={() => setForm({ ...form, languages: toggle(form.languages, l) })}
                className={`pill border ${form.languages.includes(l) ? "bg-forest text-linen border-forest" : "border-taupe text-forest/70"}`}>{l}</button>
            ))}
          </div>
        </div>
        <div className="mt-4">
          <div className="text-[11px] uppercase tracking-widest text-forest/60">Availability</div>
          <div className="flex flex-wrap gap-2 mt-2">
            {AVAIL.map((a) => (
              <button key={a} onClick={() => setForm({ ...form, availability: toggle(form.availability, a) })}
                className={`pill border ${form.availability.includes(a) ? "bg-forest text-linen border-forest" : "border-taupe text-forest/70"}`}>{a}</button>
            ))}
          </div>
        </div>
        <button onClick={search} disabled={busy} className="btn-primary mt-6" data-testid="match-run">
          {busy ? "Matching…" : "Find my matches"}
        </button>
      </div>
      {results.length > 0 && (
        <div>
          <h3 className="font-serif text-xl text-forest mb-4">Top matches</h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {results.map((s) => (
              <div key={s.id} className="relative">
                <span className="absolute -top-2 -right-2 z-10 badge-gold" data-testid={`score-${s.id}`}><Sparkles className="w-3 h-3" /> {s.match_score}</span>
                <SitterCard s={s} />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ParentFavorites() {
  const { user } = useAuth();
  const [favs, setFavs] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { (async () => { try { const { data } = await api.get("/favorites"); setFavs(data); } catch (_) {} setLoading(false); })(); }, []);
  if (!user.is_premium) return <PremiumGate title="Saved caregivers is Premium" body="Keep a private shortlist of your favourite caregivers to revisit any time." />;
  if (loading) return <div className="card-organic p-10 text-forest/60 text-center">Loading…</div>;
  if (!favs.length) return <div className="card-organic p-10 text-forest/60 text-center">No favourites yet — tap the heart on a caregiver's profile to save them.</div>;
  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="fav-grid">
      {favs.map((s) => <SitterCard key={s.id} s={s} />)}
    </div>
  );
}

function ParentBookings() {
  const [bookings, setBookings] = useState([]);
  const load = async () => { const { data } = await api.get("/bookings/me"); setBookings(data); };
  useEffect(() => { load(); }, []);
  const cancel = async (id) => { try { await api.patch(`/bookings/${id}`, { status: "cancelled" }); toast.success("Cancelled"); load(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };
  const complete = async (id) => { try { await api.patch(`/bookings/${id}`, { status: "completed" }); toast.success("Marked completed"); load(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };
  if (!bookings.length) return <div className="card-organic p-10 text-center text-forest/60">No bookings yet. Browse caregivers to send your first request.</div>;
  return <div className="grid gap-4" data-testid="parent-bookings">{bookings.map((b) => <BookingRow key={b.id} b={b} role="parent" onCancel={cancel} onComplete={complete} onReviewSaved={load} />)}</div>;
}

function MembershipPanel() {
  const { user, refresh } = useAuth();
  const cancel = async () => {
    if (!confirm("Cancel your Premium membership?")) return;
    try { await api.post("/subscription/cancel"); await refresh(); toast.success("Membership cancelled"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <div className="card-organic p-8" data-testid="membership-panel">
      <div className="flex items-center gap-2">
        <Sparkles className="w-5 h-5 text-gold" />
        <h2 className="font-serif text-2xl text-forest">Membership</h2>
      </div>
      <div className="mt-4">
        <div className="text-sm text-forest/60">Current plan</div>
        <div className="font-serif text-3xl text-forest mt-1">{user.is_premium ? "Premium" : "Free"}</div>
        {user.is_premium && user.membership_end && (
          <div className="text-sm text-forest/60 mt-1">Renews {new Date(user.membership_end).toLocaleDateString()}</div>
        )}
      </div>
      <div className="mt-6 flex gap-3 flex-wrap">
        {user.is_premium
          ? <button onClick={cancel} className="btn-outline" data-testid="cancel-membership">Cancel Premium</button>
          : <Link to="/pricing" className="btn-primary" data-testid="see-pricing">Upgrade to Premium</Link>}
      </div>
    </div>
  );
}

// ---- Caregiver Dashboard ----
function CaregiverDashboard() {
  const [tab, setTab] = useState("overview");
  const tabs = [
    { k: "overview", l: "Overview", icon: TrendingUp },
    { k: "requests", l: "Booking requests", icon: CalendarCheck },
    { k: "calendar", l: "Calendar", icon: Clock },
    { k: "messages", l: "Messages", icon: MessageCircle },
    { k: "reviews", l: "Reviews", icon: Star },
    { k: "earnings", l: "Earnings", icon: Wallet },
    { k: "profile", l: "Profile", icon: User },
  ];
  return (
    <section data-testid="sitter-dash" className="space-y-6">
      <div className="flex flex-wrap gap-2 p-1 bg-taupe/30 rounded-full w-fit">
        {tabs.map((t) => (
          <button key={t.k} onClick={() => setTab(t.k)} data-testid={`ctab-${t.k}`}
            className={`px-4 py-2 rounded-full text-sm flex items-center gap-1.5 transition-colors ${tab === t.k ? "bg-forest text-linen" : "text-forest/70"}`}>
            <t.icon className="w-4 h-4" /> {t.l}
          </button>
        ))}
      </div>
      {tab === "overview" && <CaregiverOverview onGo={setTab} />}
      {tab === "requests" && <CaregiverRequests />}
      {tab === "calendar" && <CaregiverCalendar />}
      {tab === "messages" && <MessagesPanel />}
      {tab === "reviews" && <CaregiverReviews />}
      {tab === "earnings" && <CaregiverEarnings />}
      {tab === "profile" && <CaregiverProfile />}
    </section>
  );
}

function CaregiverOverview({ onGo }) {
  const [profile, setProfile] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [earnings, setEarnings] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const [{ data: p }, { data: b }, { data: e }] = await Promise.all([
          api.get("/babysitters/me/profile"), api.get("/bookings/me"), api.get("/earnings/me"),
        ]);
        setProfile(p); setBookings(b); setEarnings(e);
      } catch (_) {}
    })();
  }, []);
  if (!profile) return <div className="card-organic p-8 text-forest/60">Loading…</div>;
  const pending = bookings.filter((b) => b.status === "pending").length;
  const upcoming = bookings.filter((b) => b.status === "accepted").length;
  return (
    <div className="space-y-6" data-testid="caregiver-overview">
      <div className="grid md:grid-cols-2 gap-6">
        <div className="card-organic p-8">
          <div className="text-xs uppercase tracking-widest text-forest/60">Profile completion</div>
          <div className="flex items-baseline gap-2 mt-2">
            <span className="font-serif text-5xl text-forest" data-testid="completion-pct">{profile.completion}%</span>
            {profile.verified && <span className="badge-gold"><ShieldCheck className="w-3 h-3" /> Verified</span>}
          </div>
          <div className="mt-4 h-2 rounded-full bg-taupe/50 overflow-hidden">
            <div className="h-full bg-forest rounded-full transition-all" style={{ width: `${profile.completion}%` }} />
          </div>
          <p className="text-sm text-forest/70 mt-3">
            {profile.completion < 100
              ? "Complete your profile to appear higher in searches."
              : "Beautiful — your profile is fully complete."}
          </p>
          <button onClick={() => onGo("profile")} className="btn-outline text-sm mt-4">Edit profile</button>
        </div>
        <div className="card-organic p-8">
          <div className="text-xs uppercase tracking-widest text-forest/60">This month's activity</div>
          <div className="grid grid-cols-3 gap-4 mt-3">
            <div><div className="font-serif text-3xl text-forest">{pending}</div><div className="text-xs text-forest/60 mt-1">Pending</div></div>
            <div><div className="font-serif text-3xl text-forest">{upcoming}</div><div className="text-xs text-forest/60 mt-1">Upcoming</div></div>
            <div><div className="font-serif text-3xl text-forest">${earnings?.this_month || 0}</div><div className="text-xs text-forest/60 mt-1">Earned</div></div>
          </div>
          <button onClick={() => onGo("requests")} className="btn-primary text-sm mt-6 flex items-center gap-1.5">
            View requests <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

function CaregiverRequests() {
  const [bookings, setBookings] = useState([]);
  const load = async () => { const { data } = await api.get("/bookings/me"); setBookings(data); };
  useEffect(() => { load(); }, []);
  const respond = async (id, status) => {
    try { await api.patch(`/bookings/${id}`, { status }); toast.success(`Booking ${status}`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const pending = bookings.filter((b) => b.status === "pending");
  const others = bookings.filter((b) => b.status !== "pending");
  return (
    <div className="space-y-6" data-testid="caregiver-requests">
      <div>
        <h2 className="font-serif text-2xl text-forest mb-3">Pending</h2>
        {!pending.length ? <div className="card-organic p-8 text-center text-forest/60">No pending requests.</div>
          : <div className="grid gap-4">{pending.map((b) => <BookingRow key={b.id} b={b} role="sitter" onAccept={() => respond(b.id, "accepted")} onDecline={() => respond(b.id, "declined")} onComplete={() => respond(b.id, "completed")} />)}</div>}
      </div>
      {others.length > 0 && (
        <div>
          <h2 className="font-serif text-2xl text-forest mb-3">Other bookings</h2>
          <div className="grid gap-4">{others.map((b) => <BookingRow key={b.id} b={b} role="sitter" onComplete={() => respond(b.id, "completed")} />)}</div>
        </div>
      )}
    </div>
  );
}

function CaregiverCalendar() {
  const [bookings, setBookings] = useState([]);
  useEffect(() => { (async () => { try { const { data } = await api.get("/bookings/me"); setBookings(data.filter((b) => ["accepted", "pending"].includes(b.status))); } catch (_) {} })(); }, []);
  const upcoming = [...bookings].sort((a, b) => new Date(a.start_datetime) - new Date(b.start_datetime));
  return (
    <div className="space-y-4" data-testid="caregiver-calendar">
      <h2 className="font-serif text-2xl text-forest">Upcoming schedule</h2>
      {!upcoming.length ? <div className="card-organic p-8 text-center text-forest/60">Your calendar is clear.</div>
        : <div className="grid gap-3">{upcoming.map((b) => (
            <div key={b.id} className="card-organic p-5 flex items-center gap-4 flex-wrap">
              <div className="w-16 text-center border-r border-taupe pr-4">
                <div className="text-[11px] uppercase tracking-widest text-forest/60">{new Date(b.start_datetime).toLocaleString(undefined, { month: "short" })}</div>
                <div className="font-serif text-3xl text-forest">{new Date(b.start_datetime).getDate()}</div>
              </div>
              <div className="flex-1 min-w-[200px]">
                <div className="font-serif text-lg text-forest">{b.parent_name}</div>
                <div className="text-sm text-forest/70 flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {new Date(b.start_datetime).toLocaleTimeString([], {hour:'2-digit',minute:'2-digit'})} → {new Date(b.end_datetime).toLocaleTimeString([], {hour:'2-digit',minute:'2-digit'})}</div>
                <div className="text-xs text-forest/60 mt-1">{b.children_count} child(ren){b.notes ? ` · ${b.notes}` : ""}</div>
              </div>
              <span className={`pill ${b.status === "accepted" ? "badge-gold" : "badge-sage"}`}>{b.status}</span>
            </div>
          ))}</div>}
    </div>
  );
}

function CaregiverReviews() {
  const [reviews, setReviews] = useState([]);
  useEffect(() => { (async () => { try { const { data } = await api.get("/reviews/mine"); setReviews(data); } catch (_) {} })(); }, []);
  if (!reviews.length) return <div className="card-organic p-8 text-center text-forest/60">No reviews yet — your first completed booking will unlock reviews.</div>;
  return (
    <div className="space-y-3" data-testid="caregiver-reviews">
      {reviews.map((r) => (
        <div key={r.id} className="card-organic p-5">
          <div className="flex items-center justify-between">
            <span className="font-serif text-lg text-forest">{r.parent_name}</span>
            <span className="flex items-center gap-0.5">
              {[...Array(5)].map((_, i) => <Star key={i} className={`w-4 h-4 ${i < r.rating ? "fill-gold text-gold" : "text-taupe"}`} />)}
            </span>
          </div>
          <p className="text-forest/80 mt-2 text-sm leading-relaxed">{r.text}</p>
        </div>
      ))}
    </div>
  );
}

function CaregiverEarnings() {
  const [e, setE] = useState(null);
  useEffect(() => { (async () => { try { const { data } = await api.get("/earnings/me"); setE(data); } catch (_) {} })(); }, []);
  if (!e) return <div className="card-organic p-8 text-forest/60">Loading…</div>;
  return (
    <div className="space-y-6" data-testid="caregiver-earnings">
      <div className="rounded-xl bg-gold/10 border border-gold/40 p-3 text-xs text-forest/70">
        <b className="text-forest">Display only.</b> Sakeenah doesn't process payments — parents pay you directly. This is an estimated tracker from your completed bookings and hourly rate.
      </div>
      <div className="grid md:grid-cols-3 gap-4">
        {[
          ["Lifetime", `$${e.total}`, `${e.count} bookings`],
          ["This month", `$${e.this_month}`, "completed"],
          ["Pending", `$${e.pending}`, "accepted / awaiting"],
        ].map(([k, v, sub]) => (
          <div key={k} className="card-organic p-6">
            <div className="text-[11px] uppercase tracking-widest text-forest/60">{k}</div>
            <div className="font-serif text-4xl text-forest mt-2">{v}</div>
            <div className="text-xs text-forest/60 mt-1">{sub}</div>
          </div>
        ))}
      </div>
      <div>
        <h3 className="font-serif text-xl text-forest mb-3">Recent activity</h3>
        {!e.recent.length ? <div className="card-organic p-8 text-forest/60">No activity yet.</div> : (
          <div className="grid gap-2">
            {e.recent.map((r) => (
              <div key={r.id} className="card-organic p-4 flex items-center justify-between flex-wrap gap-3">
                <div>
                  <div className="font-serif text-forest">{r.parent_name}</div>
                  <div className="text-xs text-forest/60">{new Date(r.date).toLocaleString()} · {r.hours}h</div>
                </div>
                <div className="text-right">
                  <div className="font-serif text-lg text-forest">${r.amount}</div>
                  <div className="text-[10px] uppercase tracking-widest text-forest/50">{r.status}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function CaregiverProfile() {
  const [profile, setProfile] = useState(null);
  const load = async () => { const { data } = await api.get("/babysitters/me/profile"); setProfile(data); };
  useEffect(() => { load(); }, []);
  if (!profile) return <div className="card-organic p-8 text-forest/60">Loading…</div>;
  return <SitterProfileForm profile={profile} onSaved={load} />;
}

function SitterProfileForm({ profile, onSaved }) {
  const [f, setF] = useState({
    headline: profile.headline || "", bio: profile.bio || "", postal_code: profile.postal_code || "",
    city: profile.city || "", hourly_rate: profile.hourly_rate || 0,
    experience_years: profile.experience_years || 0,
    languages: profile.languages || [], availability: profile.availability || [],
    cpr_certified: !!profile.cpr_certified, police_check_certified: !!profile.police_check_certified,
    cpr_doc_url: profile.cpr_doc_url || "", police_doc_url: profile.police_doc_url || "",
  });
  const [busy, setBusy] = useState(false);
  const toggle = (list, v) => list.includes(v) ? list.filter((x) => x !== v) : [...list, v];
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.put("/babysitters/me/profile", { ...f, hourly_rate: Number(f.hourly_rate), experience_years: Number(f.experience_years) });
      toast.success("Profile updated"); onSaved();
    } catch (er) { toast.error(formatApiErrorDetail(er.response?.data?.detail)); }
    setBusy(false);
  };
  return (
    <form onSubmit={submit} className="card-organic p-6 md:p-8 space-y-5" data-testid="sitter-profile-form">
      <div className="flex items-center gap-2">
        <h2 className="font-serif text-2xl text-forest">Your profile</h2>
        <span className="badge-sage">{profile.completion}% complete</span>
        {profile.verified ? <span className="badge-gold"><ShieldCheck className="w-3 h-3" /> Verified</span> : <span className="badge-sage">Pending review</span>}
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="text-[11px] uppercase tracking-widest text-forest/60">Headline</label>
          <input value={f.headline} onChange={(e) => setF({ ...f, headline: e.target.value })} className="input-organic mt-1" data-testid="pf-headline" />
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-widest text-forest/60">Years of experience</label>
          <input type="number" min="0" value={f.experience_years} onChange={(e) => setF({ ...f, experience_years: e.target.value })} className="input-organic mt-1" data-testid="pf-exp" />
        </div>
        <div className="md:col-span-2">
          <label className="text-[11px] uppercase tracking-widest text-forest/60">Bio</label>
          <textarea rows="4" value={f.bio} onChange={(e) => setF({ ...f, bio: e.target.value })} className="input-organic mt-1" data-testid="pf-bio" />
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-widest text-forest/60">Postal code</label>
          <input value={f.postal_code} onChange={(e) => setF({ ...f, postal_code: e.target.value.toUpperCase() })} className="input-organic mt-1" data-testid="pf-postal" />
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-widest text-forest/60">City</label>
          <input value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} className="input-organic mt-1" data-testid="pf-city" />
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-widest text-forest/60">Hourly rate ($)</label>
          <input type="number" min="0" value={f.hourly_rate} onChange={(e) => setF({ ...f, hourly_rate: e.target.value })} className="input-organic mt-1" data-testid="pf-rate" />
        </div>
      </div>
      <div>
        <label className="text-[11px] uppercase tracking-widest text-forest/60">Languages</label>
        <div className="flex flex-wrap gap-2 mt-2">
          {LANGS.map((l) => (
            <button type="button" key={l} onClick={() => setF({ ...f, languages: toggle(f.languages, l) })}
              className={`pill border ${f.languages.includes(l) ? "bg-forest text-linen border-forest" : "border-taupe text-forest/70"}`}>{l}</button>
          ))}
        </div>
      </div>
      <div>
        <label className="text-[11px] uppercase tracking-widest text-forest/60">Availability</label>
        <div className="flex flex-wrap gap-2 mt-2">
          {AVAIL.map((a) => (
            <button type="button" key={a} onClick={() => setF({ ...f, availability: toggle(f.availability, a) })}
              className={`pill border ${f.availability.includes(a) ? "bg-forest text-linen border-forest" : "border-taupe text-forest/70"}`}>{a}</button>
          ))}
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <div className="card-organic p-4">
          <label className="flex items-center gap-2 text-forest cursor-pointer">
            <input type="checkbox" checked={f.cpr_certified} onChange={(e) => setF({ ...f, cpr_certified: e.target.checked })} className="accent-forest w-4 h-4" data-testid="pf-cpr" /> CPR certified
          </label>
          <input value={f.cpr_doc_url} onChange={(e) => setF({ ...f, cpr_doc_url: e.target.value })} className="input-organic mt-3" placeholder="Certificate document URL" />
        </div>
        <div className="card-organic p-4">
          <label className="flex items-center gap-2 text-forest cursor-pointer">
            <input type="checkbox" checked={f.police_check_certified} onChange={(e) => setF({ ...f, police_check_certified: e.target.checked })} className="accent-forest w-4 h-4" data-testid="pf-police" /> Police check
          </label>
          <input value={f.police_doc_url} onChange={(e) => setF({ ...f, police_doc_url: e.target.value })} className="input-organic mt-3" placeholder="Police check document URL" />
        </div>
      </div>
      <button className="btn-primary" disabled={busy} data-testid="pf-save">{busy ? "Saving…" : "Save profile"}</button>
    </form>
  );
}

// ---- Messages ----
function MessagesPanel() {
  const { user } = useAuth();
  const [convs, setConvs] = useState([]);
  const [selected, setSelected] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const loadConvs = async () => { try { const { data } = await api.get("/messages/conversations"); setConvs(data); } catch (_) {} };
  const openConv = async (c) => {
    setSelected(c);
    try { const { data } = await api.get(`/messages/${c.conversation_id}`); setMsgs(data); loadConvs(); }
    catch (_) {}
  };
  const send = async () => {
    if (!text.trim() || !selected) return;
    try { await api.post("/messages", { to_user_id: selected.other_user_id, text }); setText(""); openConv(selected); }
    catch (er) { toast.error(formatApiErrorDetail(er.response?.data?.detail)); }
  };
  useEffect(() => { loadConvs(); }, []);

  const isFreeParent = user.role === "parent" && !user.is_premium;
  if (isFreeParent) return <PremiumGate title="Messaging is a Premium feature" body="Upgrade to chat directly with caregivers and coordinate bookings." />;

  return (
    <div className="grid md:grid-cols-3 gap-4 min-h-[500px]" data-testid="messages-panel">
      <div className="card-organic p-3 md:col-span-1 max-h-[600px] overflow-y-auto">
        {!convs.length ? <div className="p-6 text-forest/60 text-sm text-center">No conversations yet.</div> : (
          <div className="space-y-1">
            {convs.map((c) => (
              <button key={c.conversation_id} onClick={() => openConv(c)}
                className={`w-full text-left p-3 rounded-2xl flex gap-3 items-center hover:bg-taupe/40 transition-colors ${selected?.conversation_id === c.conversation_id ? "bg-taupe/50" : ""}`}
                data-testid={`conv-${c.conversation_id}`}>
                <InitialAvatar name={c.other_name} size={40} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-serif text-sm text-forest truncate">{c.other_name}</div>
                    {c.unread > 0 && <span className="badge-gold text-[10px]">{c.unread}</span>}
                  </div>
                  <div className="text-xs text-forest/60 truncate">{c.last_text}</div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="card-organic md:col-span-2 flex flex-col" style={{ maxHeight: 600 }}>
        {!selected ? <div className="flex-1 flex items-center justify-center text-forest/50 text-sm">Choose a conversation</div> : (
          <>
            <div className="p-4 border-b border-taupe flex items-center gap-2">
              <InitialAvatar name={selected.other_name} size={36} />
              <span className="font-serif text-lg text-forest">{selected.other_name}</span>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-2">
              {msgs.map((m) => {
                const mine = m.from_id === user.id;
                return (
                  <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[75%] px-4 py-2 rounded-2xl text-sm ${mine ? "bg-forest text-linen" : "bg-taupe/60 text-forest"}`}>{m.text}</div>
                  </div>
                );
              })}
              {!msgs.length && <div className="text-center text-forest/50 text-sm py-8">Say hello to start the conversation.</div>}
            </div>
            <div className="p-3 border-t border-taupe flex gap-2">
              <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
                className="input-organic" placeholder="Type a message…" data-testid="msg-input" />
              <button onClick={send} className="btn-primary text-sm px-4" data-testid="msg-send"><Send className="w-4 h-4" /></button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ---- Admin ----
function AdminDash() {
  const [sitters, setSitters] = useState([]);
  const [stats, setStats] = useState(null);
  const load = async () => {
    const [{ data: s }, { data: st }] = await Promise.all([api.get("/admin/babysitters"), api.get("/admin/stats")]);
    setSitters(s); setStats(st);
  };
  useEffect(() => { load(); }, []);
  const setVerified = async (id, verified) => {
    try { await api.patch(`/admin/babysitters/${id}/verify`, { verified }); toast.success(verified ? "Approved" : "Un-verified"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <section className="space-y-6" data-testid="admin-dash">
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
          {[["Users", stats.users], ["Parents", stats.parents], ["Premium", stats.premium_parents], ["Caregivers", stats.sitters], ["Verified", stats.verified_sitters], ["Bookings", stats.bookings]].map(([k, v]) => (
            <div key={k} className="card-organic p-5">
              <div className="text-[11px] uppercase tracking-widest text-forest/60">{k}</div>
              <div className="font-serif text-3xl text-forest mt-1">{v}</div>
            </div>
          ))}
        </div>
      )}
      <h2 className="font-serif text-2xl text-forest">Caregiver approvals</h2>
      <div className="grid gap-3">
        {sitters.map((s) => (
          <div key={s.id} className="card-organic p-4 flex items-center gap-4 flex-wrap" data-testid={`admin-sitter-${s.id}`}>
            <InitialAvatar name={s.name} size={56} />
            <div className="flex-1 min-w-[200px]">
              <div className="font-serif text-lg text-forest">{s.name}</div>
              <div className="text-xs text-forest/60">{s.email} · {s.postal_code || "no postal"} · {s.city || ""}</div>
              <div className="flex gap-1.5 mt-1.5 flex-wrap">
                {s.cpr_certified && <span className="badge-sage">CPR</span>}
                {s.police_check_certified && <span className="badge-sage">Police</span>}
                {s.verified && <span className="badge-gold"><ShieldCheck className="w-3 h-3" /> Verified</span>}
                <span className="badge-sage">{s.completion}%</span>
              </div>
            </div>
            <div className="flex gap-2">
              {s.verified
                ? <button onClick={() => setVerified(s.id, false)} className="btn-outline text-sm" data-testid={`unverify-${s.id}`}>Un-verify</button>
                : <button onClick={() => setVerified(s.id, true)} className="btn-primary text-sm" data-testid={`verify-${s.id}`}>Approve</button>}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ---- Shared Booking Row ----
function BookingRow({ b, role, onAccept, onDecline, onCancel, onComplete, onReviewSaved }) {
  const [showReview, setShowReview] = useState(false);
  const statusColor = {
    pending: "badge-sage", accepted: "badge-gold", declined: "bg-taupe text-forest/70 pill",
    cancelled: "bg-taupe text-forest/70 pill", completed: "bg-sage/40 text-forest pill",
  }[b.status] || "badge-sage";
  return (
    <div className="card-organic p-5" data-testid={`booking-${b.id}`}>
      <div className="flex items-start gap-4 flex-wrap">
        <InitialAvatar name={role === "sitter" ? b.parent_name : b.sitter_name} size={56} />
        <div className="flex-1 min-w-[220px]">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-serif text-lg text-forest">{role === "sitter" ? b.parent_name : b.sitter_name}</span>
            <span className={statusColor} data-testid={`status-${b.id}`}>{b.status}</span>
          </div>
          <div className="text-sm text-forest/70 mt-1 flex items-center gap-1.5">
            <Clock className="w-3.5 h-3.5" /> {new Date(b.start_datetime).toLocaleString()} → {new Date(b.end_datetime).toLocaleString()}
          </div>
          <div className="text-sm text-forest/70">{b.children_count} child(ren){b.notes ? ` · ${b.notes}` : ""}</div>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          {role === "sitter" && b.status === "pending" && (
            <>
              <button onClick={onAccept} className="btn-primary text-sm flex items-center gap-1" data-testid={`accept-${b.id}`}><Check className="w-4 h-4" /> Accept</button>
              <button onClick={onDecline} className="btn-outline text-sm flex items-center gap-1" data-testid={`decline-${b.id}`}><X className="w-4 h-4" /> Decline</button>
            </>
          )}
          {role === "sitter" && b.status === "accepted" && (
            <button onClick={onComplete} className="btn-outline text-sm">Mark completed</button>
          )}
          {role === "parent" && (b.status === "pending" || b.status === "accepted") && (
            <>
              {b.status === "accepted" && <button onClick={() => onComplete(b.id)} className="btn-outline text-sm">Mark completed</button>}
              <button onClick={() => onCancel(b.id)} className="btn-outline text-sm" data-testid={`cancel-${b.id}`}>Cancel</button>
            </>
          )}
          {role === "parent" && b.status === "completed" && !showReview && (
            <button onClick={() => setShowReview(true)} className="btn-primary text-sm" data-testid={`review-btn-${b.id}`}>Leave review</button>
          )}
        </div>
      </div>
      {showReview && <ReviewForm bookingId={b.id} onDone={() => { setShowReview(false); onReviewSaved && onReviewSaved(); }} />}
    </div>
  );
}

function ReviewForm({ bookingId, onDone }) {
  const [rating, setRating] = useState(5);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.post("/reviews", { booking_id: bookingId, rating, text }); toast.success("Thank you"); onDone(); }
    catch (er) { toast.error(formatApiErrorDetail(er.response?.data?.detail)); }
    setBusy(false);
  };
  return (
    <form onSubmit={submit} className="mt-4 border-t border-taupe pt-4 space-y-3">
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((i) => (
          <button type="button" key={i} onClick={() => setRating(i)}>
            <Star className={`w-6 h-6 ${i <= rating ? "fill-gold text-gold" : "text-taupe"}`} />
          </button>
        ))}
      </div>
      <textarea rows="3" value={text} onChange={(e) => setText(e.target.value)} className="input-organic" placeholder="Share your family's experience…" />
      <button className="btn-primary text-sm" disabled={busy}>{busy ? "Sending…" : "Submit review"}</button>
    </form>
  );
}
